import { useCallback, useEffect, useRef, useState } from 'react';

import { initObservability } from '../helpers/observability';

const RECOVERY_URL_KEY = 'opendoor.cosmos.clientErrorRecoveryUrl';

export const shouldReloadAfterClientError = (
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  currentUrl: string,
): boolean => {
  if (storage.getItem(RECOVERY_URL_KEY) === currentUrl) {
    return false;
  }

  storage.setItem(RECOVERY_URL_KEY, currentUrl);
  return true;
};

export const clearClientErrorRecoveryAttempt = (storage: Pick<Storage, 'removeItem'>): void => {
  storage.removeItem(RECOVERY_URL_KEY);
};

export const ClientErrorRecoveryReset = () => {
  useEffect(() => {
    try {
      clearClientErrorRecoveryAttempt(window.sessionStorage);
    } catch {
      // Successful rendering does not depend on recovery bookkeeping.
    }
  }, []);

  return null;
};

export const ClientErrorRecovery = () => {
  const [showFallback, setShowFallback] = useState(false);
  const reloadRequested = useRef(false);

  const retry = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    let reloadTimeout: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    try {
      if (
        reloadRequested.current ||
        shouldReloadAfterClientError(window.sessionStorage, window.location.href)
      ) {
        reloadRequested.current = true;
        const reload = () => {
          clearTimeout(reloadTimeout);
          if (!cancelled) {
            cancelled = true;
            window.location.reload();
          }
        };
        // Give queued errors a chance to reach the SDK without blocking recovery on a failed download.
        reloadTimeout = setTimeout(reload, 2000);
        void initObservability().then(reload);
        return () => {
          cancelled = true;
          clearTimeout(reloadTimeout);
        };
      }
    } catch {
      // Without storage, an automatic reload could loop on a persistent error.
    }

    setShowFallback(true);
    return undefined;
  }, []);

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-subheader-lg">{showFallback ? 'Something went wrong' : 'Refreshing…'}</h1>
      {showFallback && (
        <button
          className="rounded-full bg-od-bg-accent-brand px-6 py-3 text-label-md text-od-content-inverse-primary"
          onClick={retry}
          type="button"
        >
          Try again
        </button>
      )}
    </main>
  );
};
